import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { useBookingFormPersistence } from '../hooks/useFormPersistence';
import { formatDateWithWeekday, formatShortDate, formatDate, formatTime, createLocalISOString } from '../utils/dateFormat';
import { getVisitorId } from '../utils/visitor';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Mock doctor data for development fallback
const mockDoctors = {
  1: {
    id: 1,
    name: 'Dr. Sarah Mitchell',
    specialty: 'General Practice',
    location: 'London, UK',
    rating: 4.9,
    reviewCount: 127,
    bio: 'Dr. Sarah Mitchell is a highly experienced general practitioner with over 15 years of experience in family medicine.',
    services: [
      { id: 's1', name: 'General Consultation', duration: 30, price: 50 },
      { id: 's2', name: 'Health Check-up', duration: 60, price: 100 },
    ]
  },
  2: {
    id: 2,
    name: 'Dr. James Wilson',
    specialty: 'Cardiology',
    location: 'Manchester, UK',
    rating: 4.8,
    reviewCount: 89,
    bio: 'Dr. James Wilson is a board-certified cardiologist specializing in preventive cardiology and heart disease management.',
    services: [
      { id: 's3', name: 'Cardiac Consultation', duration: 45, price: 120 },
      { id: 's4', name: 'ECG Test', duration: 30, price: 80 },
    ]
  },
};

// Test card numbers for payment simulation
const TEST_CARDS = {
  success: '4242 4242 4242 4242',
  decline: '4000 0000 0000 0002',
  insufficient: '4000 0000 0000 9995',
  expired: '4000 0000 0000 0069',
};

// Simulate payment processing with error handling
const simulatePayment = async (cardNumber) => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1500));

  // Clean card number
  const cleanCard = cardNumber.replace(/\s/g, '');

  // Simulate different card decline scenarios based on test card numbers
  if (cleanCard === '4000000000000002') {
    return { success: false, error: { code: 'card_declined', message: 'Your card was declined. Please try a different payment method.' } };
  }
  if (cleanCard === '4000000000009995') {
    return { success: false, error: { code: 'insufficient_funds', message: 'Your card has insufficient funds. Please try a different card.' } };
  }
  if (cleanCard === '4000000000000069') {
    return { success: false, error: { code: 'expired_card', message: 'Your card has expired. Please use a different card.' } };
  }
  if (cleanCard === '4242424242424242') {
    return { success: true, paymentId: 'pi_simulated_' + Date.now() };
  }
  // For any other card, simulate a generic decline
  if (cleanCard.length !== 16) {
    return { success: false, error: { code: 'invalid_number', message: 'The card number is invalid. Please check and try again.' } };
  }
  return { success: false, error: { code: 'processing_error', message: 'An error occurred while processing your card. Please try again.' } };
};

// Fallback time slots
const fallbackSlots = [
  { time: '09:00', display: '09:00', available: true },
  { time: '09:30', display: '09:30', available: true },
  { time: '10:00', display: '10:00', available: true },
  { time: '10:30', display: '10:30', available: true },
  { time: '11:00', display: '11:00', available: true },
  { time: '11:30', display: '11:30', available: true },
  { time: '14:00', display: '14:00', available: true },
  { time: '14:30', display: '14:30', available: true },
  { time: '15:00', display: '15:00', available: true },
  { time: '15:30', display: '15:30', available: true },
  { time: '16:00', display: '16:00', available: true },
  { time: '16:30', display: '16:30', available: true }
];

// Payment Form Component with Card Input
const PaymentForm = ({ doctor, service, selectedDate, selectedTime, onSuccess, onCancel, primaryButtonStyle, embedPrimaryColor, isEmbedMode, bookingSource }) => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({}); // Field-level validation errors
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');

  // Fallback to URL parameters if props are empty (handles race condition during page load)
  const searchParams = new URLSearchParams(window.location.search);
  const actualSelectedTime = selectedTime || searchParams.get('time') || '';
  const actualSelectedDate = selectedDate || searchParams.get('date') || '';

  // Ref to prevent double-click submissions (state updates may not be immediate)
  const isSubmittingRef = React.useRef(false);

  // Use form persistence hook for guest info
  const {
    formData: guestInfo,
    updateField,
    clearFormData,
    hasRestoredData
  } = useBookingFormPersistence(doctor?.id);

  // Wrapper to update guest info (for compatibility with existing code)
  const setGuestInfo = useCallback((updater) => {
    if (typeof updater === 'function') {
      const prev = guestInfo;
      const next = updater(prev);
      Object.entries(next).forEach(([key, value]) => {
        if (prev[key] !== value) {
          updateField(key, value);
        }
      });
    } else {
      Object.entries(updater).forEach(([key, value]) => {
        updateField(key, value);
      });
    }
  }, [guestInfo, updateField]);

  // Format card number with spaces
  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const parts = [];
    for (let i = 0; i < v.length && i < 16; i += 4) {
      parts.push(v.substring(i, i + 4));
    }
    return parts.join(' ');
  };

  // Format expiry date
  const formatExpiry = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) return v.substring(0, 2) + '/' + v.substring(2, 4);
    return v;
  };

  // Phone number validation function
  const isValidPhoneNumber = (phone) => {
    if (!phone || phone.trim() === '') return true; // Phone is optional
    // Valid phone: must contain at least 7 digits, can have spaces, dashes, parentheses, plus sign
    // Examples: +44 20 7946 0958, (020) 7946-0958, 07911 123456, +1-555-123-4567
    const digitsOnly = phone.replace(/\D/g, '');
    const hasValidFormat = /^[\d\s\-+()]+$/.test(phone);
    return hasValidFormat && digitsOnly.length >= 7;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // DOUBLE-CLICK PREVENTION: Check if already submitting (ref is synchronous, immediate)
    if (isSubmittingRef.current || loading) {
      console.log('[DOUBLE-CLICK PREVENTED] Payment form submission already in progress');
      return;
    }

    // Immediately mark as submitting to prevent any further clicks
    isSubmittingRef.current = true;
    setError(null);
    setFieldErrors({});
    setLoading(true);

    // Validate guest info if not logged in - field-level validation
    if (!user) {
      const errors = {};
      if (!guestInfo.name || guestInfo.name.trim() === '') {
        errors.name = 'Name is required';
      }
      if (!guestInfo.email || guestInfo.email.trim() === '') {
        errors.email = 'Email is required';
      }
      // Validate phone number format if provided
      if (guestInfo.phone && !isValidPhoneNumber(guestInfo.phone)) {
        errors.phone = 'Please enter a valid phone number (digits, spaces, dashes, and parentheses only)';
      }

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setError('Please fill in all required fields');
        setLoading(false);
        isSubmittingRef.current = false;
        return;
      }
    }

    // Validate card details
    if (!cardNumber || cardNumber.replace(/\s/g, '').length < 16) {
      setError('Please enter a valid card number');
      setLoading(false);
      isSubmittingRef.current = false;
      return;
    }
    if (!cardExpiry || cardExpiry.length < 5) {
      setError('Please enter a valid expiry date');
      setLoading(false);
      isSubmittingRef.current = false;
      return;
    }
    if (!cardCvc || cardCvc.length < 3) {
      setError('Please enter a valid CVC');
      setLoading(false);
      isSubmittingRef.current = false;
      return;
    }

    try {
      // First, create a payment intent to verify the amount is correctly converted to pence
      const paymentIntentHeaders = {
        'Content-Type': 'application/json'
      };
      if (token) {
        paymentIntentHeaders['Authorization'] = `Bearer ${token}`;
      }

      const paymentIntentResponse = await fetch(`${API_URL}/appointments/create-payment-intent`, {
        method: 'POST',
        headers: paymentIntentHeaders,
        body: JSON.stringify({
          doctorId: doctor.id,
          serviceId: service.id,
          amount: service.price,
          currency: 'gbp'
        })
      });

      if (paymentIntentResponse.ok) {
        const paymentIntentData = await paymentIntentResponse.json();
        // Log the payment intent details for verification
        console.log('[PAYMENT INTENT] Created with amount in pence:', service.price * 100, '(£' + service.price + ')');
        console.log('[PAYMENT INTENT] Response:', paymentIntentData);
      } else {
        // If payment intent creation fails (e.g., Stripe not configured), continue with simulated payment
        console.log('[PAYMENT INTENT] Backend returned error, proceeding with simulated payment');
      }

      // Simulate payment processing (for demo/test purposes)
      const paymentResult = await simulatePayment(cardNumber);

      if (!paymentResult.success) {
        // Payment failed - show error
        throw new Error(paymentResult.error.message);
      }

      // Payment succeeded - create appointment via API
      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Build appointment data - only include paymentIntentId if it's a real Stripe payment
      // (simulated payments have IDs starting with 'pi_simulated_')
      const isSimulatedPayment = paymentResult.paymentId?.startsWith('pi_simulated_');

      console.log('[PAYMENT] selectedTime:', actualSelectedTime, 'selectedDate:', actualSelectedDate);
      console.log('[PAYMENT] service:', service);
      const startTimeValue = actualSelectedTime.includes('T') ? actualSelectedTime : createLocalISOString(actualSelectedDate, actualSelectedTime);
      console.log('[PAYMENT] Calculated startTime:', startTimeValue);

      const appointmentResponse = await fetch(`${API_URL}/appointments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          doctorId: doctor.id,
          serviceId: service.id,
          // Use the full UTC time if it's an ISO string, otherwise construct from date/time
          startTime: startTimeValue,
          // Use undefined instead of null for optional fields (zod .optional() doesn't accept null)
          guestName: user ? undefined : guestInfo.name,
          guestEmail: user ? undefined : guestInfo.email,
          guestPhone: user ? undefined : guestInfo.phone,
          reasonForVisit: guestInfo.reason || undefined,
          // Don't send simulated payment IDs to backend (they can't be verified by Stripe)
          paymentIntentId: isSimulatedPayment ? undefined : paymentResult.paymentId,
          paymentAmount: service.price,
          bookingSource: bookingSource || 'direct',
        })
      });

      const appointmentData = await appointmentResponse.json();

      if (!appointmentResponse.ok) {
        // Handle subscription limit error specifically
        if (appointmentResponse.status === 403 && appointmentData.code === 'SUBSCRIPTION_LIMIT_REACHED') {
          throw new Error(appointmentData.error);
        }
        // Handle zod validation errors (which return an object)
        const errorMsg = typeof appointmentData.error === 'string'
          ? appointmentData.error
          : (appointmentData.error?.issues?.[0]?.message || 'Failed to create appointment');
        throw new Error(errorMsg);
      }

      // Clear persisted form data on successful booking
      clearFormData();
      onSuccess(appointmentData.appointment);
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Restored Data Notification */}
      {hasRestoredData && !user && (
        <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl" data-testid="form-restored-notification">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-blue-400 font-medium">Form Data Restored</p>
              <p className="text-blue-400/80 text-sm mt-1">Your previously entered information has been restored.</p>
            </div>
            <button
              type="button"
              onClick={clearFormData}
              className="text-blue-400/60 hover:text-blue-400 text-sm underline"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl" data-testid="payment-error">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-red-400 font-medium">Payment Failed</p>
              <p className="text-red-400/80 text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Booking Summary */}
      <div className="p-4 bg-slate-100 dark:bg-silver/5 rounded-xl border border-light-border dark:border-border">
        <h4 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-3">Booking Summary</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-light-text-secondary dark:text-silver/60">Service</span><span className="text-light-text dark:text-silver">{service.name}</span></div>
          <div className="flex justify-between"><span className="text-light-text-secondary dark:text-silver/60">Date</span><span className="text-light-text dark:text-silver">{formatShortDate(actualSelectedDate)}</span></div>
          <div className="flex justify-between"><span className="text-light-text-secondary dark:text-silver/60">Time</span><span className="text-light-text dark:text-silver">{actualSelectedTime.includes('T') ? formatTime(actualSelectedTime) : actualSelectedTime}</span></div>
          <div className="flex justify-between"><span className="text-light-text-secondary dark:text-silver/60">Duration</span><span className="text-light-text dark:text-silver">{service.duration || service.durationMinutes} minutes</span></div>
          <div className="border-t border-light-border dark:border-border my-2 pt-2">
            <div className="flex justify-between font-medium"><span className="text-light-text dark:text-silver">Total</span><span className="text-light-text dark:text-pure-white">£{service.price}</span></div>
          </div>
        </div>
      </div>

      {/* Patient Info (logged in) - Pre-filled and read-only */}
      {user && (
        <div className="space-y-4" data-testid="patient-info-section">
          <h4 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60">Your Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="patient-name" className="text-sm text-light-text-secondary dark:text-silver/60 block mb-1">Full Name</label>
              <input
                id="patient-name"
                type="text"
                value={`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.name || 'N/A'}
                readOnly
                className="input-field bg-slate-100 dark:bg-silver/10 cursor-not-allowed"
                data-testid="patient-name-input"
              />
            </div>
            <div>
              <label htmlFor="patient-email" className="text-sm text-light-text-secondary dark:text-silver/60 block mb-1">Email Address</label>
              <input
                id="patient-email"
                type="email"
                value={user.email || 'N/A'}
                readOnly
                className="input-field bg-slate-100 dark:bg-silver/10 cursor-not-allowed"
                data-testid="patient-email-input"
              />
            </div>
          </div>
          <p className="text-xs text-slate-400 dark:text-silver/50">Booking as {user.email}. Your account details will be used for this booking.</p>
        </div>
      )}

      {/* Guest Info (if not logged in) */}
      {!user && (
        <div className="space-y-4">
          <h4 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60">Your Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="guest-name" className="sr-only">Full Name (required)</label>
              <input
                id="guest-name"
                type="text"
                placeholder="Full Name *"
                value={guestInfo.name}
                onChange={(e) => {
                  setGuestInfo(prev => ({ ...prev, name: e.target.value }));
                  if (fieldErrors.name) setFieldErrors(prev => ({ ...prev, name: null }));
                }}
                aria-required="true"
                aria-invalid={fieldErrors.name ? 'true' : 'false'}
                aria-describedby={fieldErrors.name ? 'name-error' : undefined}
                className={`input-field ${fieldErrors.name ? 'border-red-500 focus:border-red-500' : ''}`}
                data-testid="guest-name-input"
              />
              {fieldErrors.name && (
                <p id="name-error" className="text-red-400 text-xs mt-1" data-testid="name-error">{fieldErrors.name}</p>
              )}
            </div>
            <div>
              <label htmlFor="guest-email" className="sr-only">Email Address (required)</label>
              <input
                id="guest-email"
                type="email"
                placeholder="Email *"
                value={guestInfo.email}
                onChange={(e) => {
                  setGuestInfo(prev => ({ ...prev, email: e.target.value }));
                  if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: null }));
                }}
                aria-required="true"
                aria-invalid={fieldErrors.email ? 'true' : 'false'}
                aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                className={`input-field ${fieldErrors.email ? 'border-red-500 focus:border-red-500' : ''}`}
                data-testid="guest-email-input"
              />
              {fieldErrors.email && (
                <p id="email-error" className="text-red-400 text-xs mt-1" data-testid="email-error">{fieldErrors.email}</p>
              )}
            </div>
          </div>
          <div>
            <label htmlFor="guest-phone" className="sr-only">Phone Number (optional)</label>
            <input
              id="guest-phone"
              type="tel"
              placeholder="Phone Number (optional)"
              value={guestInfo.phone}
              onChange={(e) => {
                setGuestInfo(prev => ({ ...prev, phone: e.target.value }));
                if (fieldErrors.phone) setFieldErrors(prev => ({ ...prev, phone: null }));
              }}
              aria-invalid={fieldErrors.phone ? 'true' : 'false'}
              aria-describedby={fieldErrors.phone ? 'phone-error' : undefined}
              className={`input-field ${fieldErrors.phone ? 'border-red-500 focus:border-red-500' : ''}`}
              data-testid="guest-phone-input"
            />
            {fieldErrors.phone && (
              <p id="phone-error" className="text-red-400 text-xs mt-1" data-testid="phone-error">{fieldErrors.phone}</p>
            )}
          </div>
        </div>
      )}

      {/* Reason for Visit */}
      <div>
        <label htmlFor="reason-for-visit" className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">Reason for Visit (optional)</label>
        <textarea id="reason-for-visit" placeholder="Briefly describe your reason for the appointment..." value={guestInfo.reason} onChange={(e) => setGuestInfo(prev => ({ ...prev, reason: e.target.value }))} className="input-field min-h-[80px] resize-none" rows={3} />
      </div>

      {/* Payment Card Details */}
      <div className="space-y-4">
        <h4 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60">Card Details</h4>
        <div>
          <label htmlFor="card-number" className="text-sm text-light-text-secondary dark:text-silver/60 block mb-1">Card Number</label>
          <input id="card-number" type="text" placeholder="4242 4242 4242 4242" value={cardNumber} onChange={(e) => setCardNumber(formatCardNumber(e.target.value))} maxLength={19} className="input-field" data-testid="card-number" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="card-expiry" className="text-sm text-light-text-secondary dark:text-silver/60 block mb-1">Expiry Date</label>
            <input id="card-expiry" type="text" placeholder="MM/YY" value={cardExpiry} onChange={(e) => setCardExpiry(formatExpiry(e.target.value))} maxLength={5} className="input-field" data-testid="card-expiry" />
          </div>
          <div>
            <label htmlFor="card-cvc" className="text-sm text-light-text-secondary dark:text-silver/60 block mb-1">CVC</label>
            <input id="card-cvc" type="text" placeholder="123" value={cardCvc} onChange={(e) => setCardCvc(e.target.value.replace(/\D/g, '').slice(0, 4))} maxLength={4} className="input-field" data-testid="card-cvc" />
          </div>
        </div>
        <div className="p-3 bg-slate-100 dark:bg-silver/5 rounded-lg text-xs text-light-text-secondary dark:text-silver/60">
          <p className="font-medium text-light-text-secondary dark:text-silver/80 mb-2">Test Cards:</p>
          <ul className="space-y-1">
            <li><span className="font-mono">{TEST_CARDS.success}</span> - Successful payment</li>
            <li><span className="font-mono">{TEST_CARDS.decline}</span> - Card declined</li>
            <li><span className="font-mono">{TEST_CARDS.insufficient}</span> - Insufficient funds</li>
            <li><span className="font-mono">{TEST_CARDS.expired}</span> - Expired card</li>
          </ul>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-4">
        <button type="button" onClick={onCancel} className="flex-1 py-3 px-6 border border-light-border dark:border-border rounded-xl text-light-text dark:text-silver hover:bg-slate-100 dark:hover:bg-silver/5 transition-smooth" disabled={loading}>Cancel</button>
        <button type="submit" disabled={loading} className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed" style={primaryButtonStyle} data-testid="pay-button">
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" aria-hidden="true"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
              Processing...
            </span>
          ) : ('Pay £' + service.price)}
        </button>
      </div>

      {/* Retry hint when there's an error */}
      {error && (
        <p className="text-center text-sm text-light-text-secondary dark:text-silver/60" data-testid="retry-hint">Please check your card details and try again, or use a different payment method.</p>
      )}
    </form>
  );
};

// Simple Booking Form Component
const SimpleBookingForm = ({ doctor, service, selectedDate, selectedTime, onSuccess, onCancel, onSlotUnavailable }) => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({}); // Field-level validation errors

  // Ref to prevent double-click submissions (state updates may not be immediate)
  const isSubmittingRef = React.useRef(false);

  // Use form persistence hook for guest info
  const {
    formData: guestInfo,
    updateField,
    clearFormData,
    hasRestoredData
  } = useBookingFormPersistence(doctor?.id);

  // Wrapper to update guest info (for compatibility with existing code)
  const setGuestInfo = useCallback((updater) => {
    if (typeof updater === 'function') {
      const prev = guestInfo;
      const next = updater(prev);
      Object.entries(next).forEach(([key, value]) => {
        if (prev[key] !== value) {
          updateField(key, value);
        }
      });
    } else {
      Object.entries(updater).forEach(([key, value]) => {
        updateField(key, value);
      });
    }
  }, [guestInfo, updateField]);

  // Phone number validation function
  const isValidPhoneNumber = (phone) => {
    if (!phone || phone.trim() === '') return true; // Phone is optional
    // Valid phone: must contain at least 7 digits, can have spaces, dashes, parentheses, plus sign
    // Examples: +44 20 7946 0958, (020) 7946-0958, 07911 123456, +1-555-123-4567
    const digitsOnly = phone.replace(/\D/g, '');
    const hasValidFormat = /^[\d\s\-+()]+$/.test(phone);
    return hasValidFormat && digitsOnly.length >= 7;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // DOUBLE-CLICK PREVENTION: Check if already submitting (ref is synchronous, immediate)
    if (isSubmittingRef.current || loading) {
      console.log('[DOUBLE-CLICK PREVENTED] Booking form submission already in progress');
      return;
    }

    // Immediately mark as submitting to prevent any further clicks
    isSubmittingRef.current = true;
    setError(null);
    setFieldErrors({});
    setLoading(true);

    // Validate guest info if not logged in - field-level validation
    if (!user) {
      const errors = {};
      if (!guestInfo.name || guestInfo.name.trim() === '') {
        errors.name = 'Name is required';
      }
      if (!guestInfo.email || guestInfo.email.trim() === '') {
        errors.email = 'Email is required';
      }
      // Validate phone number format if provided
      if (guestInfo.phone && !isValidPhoneNumber(guestInfo.phone)) {
        errors.phone = 'Please enter a valid phone number (digits, spaces, dashes, and parentheses only)';
      }

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setError('Please fill in all required fields');
        setLoading(false);
        isSubmittingRef.current = false;
        return;
      }
    }

    try {
      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const appointmentResponse = await fetch(`${API_URL}/appointments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          doctorId: doctor.id,
          serviceId: service.id,
          // Use the full UTC time if it's an ISO string, otherwise construct from date/time
          startTime: selectedTime.includes('T') ? selectedTime : createLocalISOString(selectedDate, selectedTime),
          // Use undefined instead of null for optional fields (zod .optional() doesn't accept null)
          guestName: user ? undefined : guestInfo.name,
          guestEmail: user ? undefined : guestInfo.email,
          guestPhone: user ? undefined : guestInfo.phone,
          reasonForVisit: guestInfo.reason || undefined,
          bookingSource: bookingSource || 'direct'
        })
      });

      const appointmentData = await appointmentResponse.json();

      if (!appointmentResponse.ok) {
        // Handle slot unavailable error specifically (HTTP 409 Conflict)
        if (appointmentResponse.status === 409) {
          const errorMsg = appointmentData.error || 'This time slot is no longer available. Please select a different time.';
          if (onSlotUnavailable) {
            onSlotUnavailable(errorMsg);
          }
          throw new Error(errorMsg);
        }
        // Handle subscription limit error (HTTP 403)
        if (appointmentResponse.status === 403 && appointmentData.code === 'SUBSCRIPTION_LIMIT_REACHED') {
          throw new Error(appointmentData.error);
        }
        throw new Error(appointmentData.error || 'Failed to create appointment');
      }

      // Clear persisted form data on successful booking
      clearFormData();
      onSuccess(appointmentData.appointment);
    } catch (err) {
      console.error('Booking error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Restored Data Notification */}
      {hasRestoredData && !user && (
        <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl" data-testid="form-restored-notification">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-blue-400 font-medium">Form Data Restored</p>
              <p className="text-blue-400/80 text-sm mt-1">Your previously entered information has been restored.</p>
            </div>
            <button
              type="button"
              onClick={clearFormData}
              className="text-blue-400/60 hover:text-blue-400 text-sm underline"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-red-400 font-medium">Booking Failed</p>
              <p className="text-red-400/80 text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Booking Summary */}
      <div className="p-4 bg-slate-100 dark:bg-silver/5 rounded-xl border border-light-border dark:border-border">
        <h4 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-3">Booking Summary</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-light-text-secondary dark:text-silver/60">Service</span>
            <span className="text-light-text dark:text-silver">{service.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-light-text-secondary dark:text-silver/60">Date</span>
            <span className="text-light-text dark:text-silver">{formatShortDate(selectedDate)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-light-text-secondary dark:text-silver/60">Time</span>
            <span className="text-light-text dark:text-silver">{selectedTime.includes('T') ? formatTime(selectedTime) : selectedTime}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-light-text-secondary dark:text-silver/60">Duration</span>
            <span className="text-light-text dark:text-silver">{service.duration || service.durationMinutes} minutes</span>
          </div>
          <div className="border-t border-light-border dark:border-border my-2 pt-2">
            <div className="flex justify-between font-medium">
              <span className="text-light-text dark:text-silver">Total</span>
              <span className="text-light-text dark:text-pure-white">£{service.price}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Patient Info (logged in) - Pre-filled and read-only */}
      {user && (
        <div className="space-y-4" data-testid="simple-patient-info-section">
          <h4 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60">Your Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="simple-patient-name" className="text-sm text-light-text-secondary dark:text-silver/60 block mb-1">Full Name</label>
              <input
                id="simple-patient-name"
                type="text"
                value={`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.name || 'N/A'}
                readOnly
                className="input-field bg-slate-100 dark:bg-silver/10 cursor-not-allowed"
                data-testid="simple-patient-name-input"
              />
            </div>
            <div>
              <label htmlFor="simple-patient-email" className="text-sm text-light-text-secondary dark:text-silver/60 block mb-1">Email Address</label>
              <input
                id="simple-patient-email"
                type="email"
                value={user.email || 'N/A'}
                readOnly
                className="input-field bg-slate-100 dark:bg-silver/10 cursor-not-allowed"
                data-testid="simple-patient-email-input"
              />
            </div>
          </div>
          <p className="text-xs text-slate-400 dark:text-silver/50">Booking as {user.email}. Your account details will be used for this booking.</p>
        </div>
      )}

      {/* Guest Info (if not logged in) */}
      {!user && (
        <div className="space-y-4">
          <h4 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60">Your Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="guest-name" className="sr-only">Full Name (required)</label>
              <input
                id="guest-name"
                type="text"
                placeholder="Full Name *"
                value={guestInfo.name}
                onChange={(e) => {
                  setGuestInfo(prev => ({ ...prev, name: e.target.value }));
                  if (fieldErrors.name) setFieldErrors(prev => ({ ...prev, name: null }));
                }}
                aria-required="true"
                aria-invalid={fieldErrors.name ? 'true' : 'false'}
                aria-describedby={fieldErrors.name ? 'name-error' : undefined}
                className={`input-field ${fieldErrors.name ? 'border-red-500 focus:border-red-500' : ''}`}
                data-testid="guest-name-input"
              />
              {fieldErrors.name && (
                <p id="name-error" className="text-red-400 text-xs mt-1" data-testid="name-error">{fieldErrors.name}</p>
              )}
            </div>
            <div>
              <label htmlFor="guest-email" className="sr-only">Email Address (required)</label>
              <input
                id="guest-email"
                type="email"
                placeholder="Email *"
                value={guestInfo.email}
                onChange={(e) => {
                  setGuestInfo(prev => ({ ...prev, email: e.target.value }));
                  if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: null }));
                }}
                aria-required="true"
                aria-invalid={fieldErrors.email ? 'true' : 'false'}
                aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                className={`input-field ${fieldErrors.email ? 'border-red-500 focus:border-red-500' : ''}`}
                data-testid="guest-email-input"
              />
              {fieldErrors.email && (
                <p id="email-error" className="text-red-400 text-xs mt-1" data-testid="email-error">{fieldErrors.email}</p>
              )}
            </div>
          </div>
          <div>
            <label htmlFor="guest-phone" className="sr-only">Phone Number (optional)</label>
            <input
              id="guest-phone"
              type="tel"
              placeholder="Phone Number (optional)"
              value={guestInfo.phone}
              onChange={(e) => {
                setGuestInfo(prev => ({ ...prev, phone: e.target.value }));
                if (fieldErrors.phone) setFieldErrors(prev => ({ ...prev, phone: null }));
              }}
              aria-invalid={fieldErrors.phone ? 'true' : 'false'}
              aria-describedby={fieldErrors.phone ? 'phone-error' : undefined}
              className={`input-field ${fieldErrors.phone ? 'border-red-500 focus:border-red-500' : ''}`}
              data-testid="guest-phone-input"
            />
            {fieldErrors.phone && (
              <p id="phone-error" className="text-red-400 text-xs mt-1" data-testid="phone-error">{fieldErrors.phone}</p>
            )}
          </div>
        </div>
      )}

      {/* Reason for Visit */}
      <div>
        <label htmlFor="simple-reason-for-visit" className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
          Reason for Visit (optional)
        </label>
        <textarea
          id="simple-reason-for-visit"
          placeholder="Briefly describe your reason for the appointment..."
          value={guestInfo.reason}
          onChange={(e) => setGuestInfo(prev => ({ ...prev, reason: e.target.value }))}
          className="input-field min-h-[80px] resize-none"
          rows={3}
        />
      </div>

      {/* Note about payment */}
      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
        <p className="text-blue-400 text-sm">
          <strong>Note:</strong> Payment will be collected at the time of your appointment.
        </p>
      </div>

      {/* Actions */}
      <div className="flex gap-4">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-3 px-6 border border-light-border dark:border-border rounded-xl text-light-text dark:text-silver hover:bg-slate-100 dark:hover:bg-silver/5 transition-smooth"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="booking-submit-button"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Booking...
            </span>
          ) : (
            'Confirm Booking'
          )}
        </button>
      </div>
    </form>
  );
};

// Star Rating Display Component
const StarRating = ({ rating, size = 'sm' }) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;

  const starSize = size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';

  for (let i = 0; i < 5; i++) {
    if (i < fullStars) {
      stars.push(
        <svg key={i} className={`${starSize} text-yellow-400`} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      );
    } else if (i === fullStars && hasHalfStar) {
      stars.push(
        <svg key={i} className={`${starSize} text-yellow-400`} fill="currentColor" viewBox="0 0 20 20">
          <defs>
            <linearGradient id={`half-${i}`}>
              <stop offset="50%" stopColor="currentColor" />
              <stop offset="50%" stopColor="#4B5563" />
            </linearGradient>
          </defs>
          <path fill={`url(#half-${i})`} d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      );
    } else {
      stars.push(
        <svg key={i} className={`${starSize} text-gray-600`} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      );
    }
  }

  return <div className="flex items-center gap-0.5">{stars}</div>;
};

// Success Modal
const SuccessModal = ({ appointment, doctorName, service, date, time, onClose }) => {
  // Format the booking reference (use first 8 characters of UUID for readability)
  const bookingRef = appointment?.id ? `CAL-${appointment.id.substring(0, 8).toUpperCase()}` : null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-light-bg dark:bg-obsidian border border-light-border dark:border-border rounded-2xl p-8 max-w-md w-full">
        {/* Success Icon */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="font-display text-2xl italic text-light-text dark:text-pure-white mb-2" data-testid="success-title">Booking Confirmed!</h3>
          <p className="text-light-text-secondary dark:text-silver/60">
            Your appointment has been successfully booked. You will receive a confirmation email shortly.
          </p>
        </div>

        {/* Booking Reference */}
        {bookingRef && (
          <div className="bg-slate-100 dark:bg-silver/5 border border-light-border dark:border-border rounded-xl p-4 mb-6 text-center" data-testid="booking-reference">
            <p className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-1">Booking Reference</p>
            <p className="font-mono text-lg text-light-text dark:text-pure-white font-medium" data-testid="booking-number">{bookingRef}</p>
          </div>
        )}

        {/* Confirmation Details */}
        <div className="bg-slate-100 dark:bg-silver/5 border border-light-border dark:border-border rounded-xl p-4 mb-6" data-testid="confirmation-details">
          <h4 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-3">Appointment Details</h4>
          <div className="space-y-2 text-sm">
            {doctorName && (
              <div className="flex justify-between">
                <span className="text-light-text-secondary dark:text-silver/60">Doctor</span>
                <span className="text-light-text dark:text-silver" data-testid="confirm-doctor">{doctorName}</span>
              </div>
            )}
            {service && (
              <div className="flex justify-between">
                <span className="text-light-text-secondary dark:text-silver/60">Service</span>
                <span className="text-light-text dark:text-silver" data-testid="confirm-service">{service.name}</span>
              </div>
            )}
            {(appointment?.start_time || date) && (
              <div className="flex justify-between">
                <span className="text-light-text-secondary dark:text-silver/60">Date</span>
                <span className="text-light-text dark:text-silver" data-testid="confirm-date">
                  {formatDateWithWeekday(appointment?.start_time || date)}
                </span>
              </div>
            )}
            {(appointment?.start_time || time) && (
              <div className="flex justify-between">
                <span className="text-light-text-secondary dark:text-silver/60">Time</span>
                <span className="text-light-text dark:text-silver" data-testid="confirm-time">
                  {appointment?.start_time ? formatTime(appointment.start_time) : time}
                </span>
              </div>
            )}
            {service && (
              <div className="border-t border-light-border dark:border-border pt-2 mt-2">
                <div className="flex justify-between font-medium">
                  <span className="text-light-text dark:text-silver">Total Paid</span>
                  <span className="text-light-text dark:text-pure-white" data-testid="confirm-total">£{service.price}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <button onClick={onClose} className="btn-primary w-full" data-testid="done-button">
          Done
        </button>
      </div>
    </div>
  );
};

// Main Doctor Profile Page
const DoctorProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token, isAuthenticated } = useAuth();

  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [showPayment, setShowPayment] = useState(false);
  const [bookedAppointment, setBookedAppointment] = useState(null);

  // Track if we've restored state from URL
  const [urlStateRestored, setUrlStateRestored] = useState(false);

  // Slot fetching state
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  // Booking error state (for unavailable slot)
  const [bookingError, setBookingError] = useState(null);

  // Recently completed booking state (for back button protection)
  const [recentlyCompletedBooking, setRecentlyCompletedBooking] = useState(null);

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  // Favorites state
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  // Embed mode detection for widget rendering
  const isEmbedMode = searchParams.get('embed') === 'true';
  const embedPrimaryColor = searchParams.get('primaryColor') ? `#${searchParams.get('primaryColor')}` : null;
  const embedSecondaryColor = searchParams.get('secondaryColor') ? `#${searchParams.get('secondaryColor')}` : null;
  const embedBgColor = searchParams.get('bgColor') ? `#${searchParams.get('bgColor')}` : null;
  const embedFontFamily = searchParams.get('fontFamily') ? decodeURIComponent(searchParams.get('fontFamily')) : null;
  const noBranding = searchParams.get('noBranding') === 'true';
  // Show "Powered by Calendiary" badge in embed mode for Free tier (when noBranding is NOT set)
  const showPoweredByBadge = isEmbedMode && !noBranding;

  // Booking source detection for analytics
  // If source is explicitly set, use it; otherwise determine from context
  // - embed mode → 'embed-widget'
  // - accessed via /doctor/:slug URL → 'link-in-bio'
  // - default/marketplace → 'direct'
  const bookingSource = searchParams.get('source') || (isEmbedMode ? 'embed-widget' : 'link-in-bio');

  // Font family style for embed mode
  const fontFamilyStyle = isEmbedMode && embedFontFamily ? {
    fontFamily: `${embedFontFamily}, sans-serif`,
  } : {};

  // Dynamic style for primary color elements in embed mode
  // Using 'background' instead of 'backgroundColor' to override CSS gradients
  const primaryButtonStyle = isEmbedMode && embedPrimaryColor ? {
    background: embedPrimaryColor,
    borderColor: embedPrimaryColor,
    color: '#ffffff',
  } : {};

  const primaryTextStyle = isEmbedMode && embedPrimaryColor ? {
    color: embedPrimaryColor,
  } : {};

  // Selected state style with primary color border and subtle background tint
  const primaryBorderStyle = isEmbedMode && embedPrimaryColor ? {
    borderColor: embedPrimaryColor,
    borderWidth: '2px',
    backgroundColor: `${embedPrimaryColor}10`, // 10% opacity tint
  } : {};

  // Secondary color styles for borders, secondary buttons, and supporting elements
  const secondaryBorderStyle = isEmbedMode && embedSecondaryColor ? {
    borderColor: embedSecondaryColor,
  } : {};

  const secondaryTextStyle = isEmbedMode && embedSecondaryColor ? {
    color: embedSecondaryColor,
  } : {};

  // Secondary button style (outline/ghost style buttons)
  const secondaryButtonStyle = isEmbedMode && embedSecondaryColor ? {
    borderColor: embedSecondaryColor,
    color: embedSecondaryColor,
  } : {};

  // Function to update URL with booking state (preserves embed widget parameters)
  const updateBookingUrl = useCallback((serviceId, date, time, step) => {
    const params = new URLSearchParams();

    // Preserve embed widget parameters
    const embedParams = ['embed', 'primaryColor', 'secondaryColor', 'bgColor', 'fontFamily', 'noBranding'];
    embedParams.forEach(param => {
      const value = searchParams.get(param);
      if (value) params.set(param, value);
    });

    // Set booking state parameters
    if (serviceId) params.set('service', serviceId);
    if (date) params.set('date', date);
    if (time) params.set('time', time);
    if (step) params.set('step', step);

    setSearchParams(params, { replace: true });
  }, [setSearchParams, searchParams]);

  // Get next 7 days for date selection
  const getAvailableDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      dates.push({
        value: date.toISOString().split('T')[0],
        display: formatShortDate(date)
      });
    }
    return dates;
  };

  // BACK BUTTON PROTECTION: Check for recently completed booking on page load
  // This detects when user navigates back after completing a booking
  useEffect(() => {
    const completedBookingKey = `booking_completed_${id}`;
    const storedBooking = sessionStorage.getItem(completedBookingKey);

    if (storedBooking) {
      try {
        const bookingData = JSON.parse(storedBooking);
        const completedAt = new Date(bookingData.completedAt);
        const now = new Date();
        const minutesSinceCompletion = (now - completedAt) / (1000 * 60);

        // Show the message if booking was completed within the last 30 minutes
        // This handles the case where user navigates back after booking
        if (minutesSinceCompletion < 30) {
          setRecentlyCompletedBooking(bookingData);
          // Clear the stored booking after showing the message once
          // (user will see it, then can proceed with new booking if desired)
        }

        // Clear old bookings (older than 30 minutes)
        if (minutesSinceCompletion >= 30) {
          sessionStorage.removeItem(completedBookingKey);
        }
      } catch (e) {
        console.error('Error parsing completed booking data:', e);
        sessionStorage.removeItem(completedBookingKey);
      }
    }
  }, [id]);

  // Fetch doctor data
  useEffect(() => {
    const fetchDoctor = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const response = await fetch(`${API_URL}/doctors/${id}`);
        if (response.ok) {
          const data = await response.json();
          // API returns doctor data directly (not nested under .doctor)
          setDoctor(data);
        } else if (response.status === 404) {
          // Doctor not found - show 404 page
          setNotFound(true);
          setDoctor(null);
        } else {
          // Other errors - check mock data for development fallback
          const mockDoctor = mockDoctors[id];
          if (mockDoctor) {
            setDoctor(mockDoctor);
          } else {
            setNotFound(true);
          }
        }
      } catch (error) {
        console.error('Error fetching doctor:', error);
        // Network error - check mock data for development fallback
        const mockDoctor = mockDoctors[id];
        if (mockDoctor) {
          setDoctor(mockDoctor);
        } else {
          setNotFound(true);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDoctor();
  }, [id]);

  // Track page view (runs once when doctor is loaded)
  useEffect(() => {
    const trackPageView = async () => {
      if (!id) return;

      try {
        // Get or create visitor ID for unique visitor tracking
        const visitorId = getVisitorId();

        await fetch(`${API_URL}/doctors/${id}/track-view`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ visitorId }),
        });
      } catch (error) {
        // Silently fail - page view tracking shouldn't interrupt user experience
        console.log('Failed to track page view:', error);
      }
    };

    trackPageView();
  }, [id]); // Only run once when component mounts or ID changes

  // Check if doctor is favorited (for authenticated patients)
  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (!isAuthenticated || !id || !token || user?.role !== 'patient') return;

      try {
        const response = await fetch(`${API_URL}/patients/favorites/${id}/check`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          setIsFavorited(data.isFavorited);
        }
      } catch (error) {
        console.error('Error checking favorite status:', error);
      }
    };

    checkFavoriteStatus();
  }, [id, isAuthenticated, token, user?.role]);

  // Toggle favorite status
  const handleToggleFavorite = async () => {
    if (!isAuthenticated || user?.role !== 'patient') {
      navigate('/login');
      return;
    }

    setFavoriteLoading(true);
    try {
      if (isFavorited) {
        // Remove from favorites
        const response = await fetch(`${API_URL}/patients/favorites/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          setIsFavorited(false);
        }
      } else {
        // Add to favorites
        const response = await fetch(`${API_URL}/patients/favorites/${id}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          setIsFavorited(true);
        }
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setFavoriteLoading(false);
    }
  };

  // Restore booking state from URL after doctor data is loaded
  useEffect(() => {
    if (!doctor || urlStateRestored) return;

    const urlServiceId = searchParams.get('service');
    const urlDate = searchParams.get('date');
    const urlTime = searchParams.get('time');
    const urlStep = searchParams.get('step');

    // Find the service from URL
    if (urlServiceId && doctor.services) {
      const service = doctor.services.find(s => s.id === urlServiceId || String(s.id) === urlServiceId);
      if (service) {
        setSelectedService(service);
      }
    }

    // Restore date if valid
    if (urlDate) {
      setSelectedDate(urlDate);
    }

    // Restore time if provided
    if (urlTime) {
      setSelectedTime(urlTime);
    }

    // Restore step (show payment if step=payment and all required fields are present)
    if (urlStep === 'payment' && urlServiceId && urlDate && urlTime) {
      // We need to wait for the service to be found
      const service = doctor.services?.find(s => s.id === urlServiceId || String(s.id) === urlServiceId);
      if (service) {
        setShowPayment(true);
      }
    }

    setUrlStateRestored(true);
  }, [doctor, urlStateRestored, searchParams]);

  // Track if we've done the initial URL sync (to skip the first sync after restoration)
  const [initialUrlSyncDone, setInitialUrlSyncDone] = useState(false);

  // Sync URL when booking state changes (after initial restoration, skip first sync to preserve restored values)
  useEffect(() => {
    if (!urlStateRestored || loading) return;

    // Skip the first sync after restoration to preserve URL params from deep link
    if (!initialUrlSyncDone) {
      setInitialUrlSyncDone(true);
      return;
    }

    const currentStep = showPayment ? 'payment' : null;
    updateBookingUrl(
      selectedService?.id,
      selectedDate,
      selectedTime,
      currentStep
    );
  }, [selectedService, selectedDate, selectedTime, showPayment, urlStateRestored, loading, updateBookingUrl, initialUrlSyncDone]);

  // Fetch reviews for this doctor
  useEffect(() => {
    const fetchReviews = async () => {
      if (!doctor || !id) return;

      setReviewsLoading(true);
      try {
        const response = await fetch(`${API_URL}/reviews/doctor/${id}`);
        if (response.ok) {
          const data = await response.json();
          setReviews(data.reviews || []);
        }
      } catch (error) {
        console.error('Error fetching reviews:', error);
      } finally {
        setReviewsLoading(false);
      }
    };

    fetchReviews();
  }, [doctor, id]);

  // Track if we're fetching slots for the first time (from URL restoration)
  // Using a ref to avoid triggering effect re-runs when this changes
  const initialSlotsFetchRef = React.useRef(true);

  // Fetch available slots when date changes
  useEffect(() => {
    const fetchSlots = async () => {
      if (!selectedDate || !id) return;

      setSlotsLoading(true);

      // Only reset selected time if this is a user-initiated date change (not URL restoration)
      if (!initialSlotsFetchRef.current) {
        setSelectedTime('');
      }

      try {
        const serviceId = selectedService?.id || '';
        const response = await fetch(
          `${API_URL}/appointments/doctor/${id}/slots?date=${selectedDate}${serviceId ? `&serviceId=${serviceId}` : ''}`
        );

        if (response.ok) {
          const data = await response.json();
          // Transform API response to include display time
          const slots = (data.slots || []).map(slot => ({
            ...slot,
            available: slot.available !== false
          }));
          // If API returns empty slots, it means doctor has no availability for this day
          // Don't fall back to mock slots - show "no available times" message instead
          setAvailableSlots(slots);
        } else {
          // Fall back to mock slots only on API error
          setAvailableSlots(fallbackSlots);
        }
      } catch (error) {
        console.error('Error fetching slots:', error);
        setAvailableSlots(fallbackSlots);
      } finally {
        setSlotsLoading(false);
        // Mark that initial fetch is done
        initialSlotsFetchRef.current = false;
      }
    };

    fetchSlots();
  }, [selectedDate, id, selectedService]);

  const handleBookNow = () => {
    if (!selectedService || !selectedDate || !selectedTime) {
      return;
    }
    setBookingError(null);
    setShowPayment(true);
  };

  const handlePaymentSuccess = (appointment) => {
    setBookedAppointment(appointment);
    setShowPayment(false);

    // BACK BUTTON PROTECTION: Store the completed booking in sessionStorage
    // This prevents form resubmission when user presses back button
    const completedBookingKey = `booking_completed_${id}`;
    sessionStorage.setItem(completedBookingKey, JSON.stringify({
      appointmentId: appointment.id,
      completedAt: new Date().toISOString(),
      service: selectedService?.name,
      date: selectedDate,
      time: selectedTime
    }));

    // Clear URL params and replace history entry to prevent back navigation to payment form
    // Using navigate with replace: true removes the payment step from browser history
    // Preserve embed widget parameters
    const embedParams = new URLSearchParams();
    ['embed', 'primaryColor', 'secondaryColor', 'bgColor', 'fontFamily', 'noBranding'].forEach(param => {
      const value = searchParams.get(param);
      if (value) embedParams.set(param, value);
    });
    const embedQuery = embedParams.toString();
    navigate(`/doctor/${id}${embedQuery ? '?' + embedQuery : ''}`, { replace: true });
  };

  const handleSlotUnavailable = async (errorMsg) => {
    setBookingError(errorMsg);
    setShowPayment(false);
    setSelectedTime('');

    // Refresh slots to show updated availability
    if (selectedDate) {
      try {
        const serviceId = selectedService?.id || '';
        const response = await fetch(
          `${API_URL}/appointments/doctor/${id}/slots?date=${selectedDate}${serviceId ? `&serviceId=${serviceId}` : ''}`
        );
        if (response.ok) {
          const data = await response.json();
          const slots = (data.slots || []).map(slot => ({
            ...slot,
            available: slot.available !== false
          }));
          setAvailableSlots(slots.length > 0 ? slots : fallbackSlots);
        }
      } catch (error) {
        console.error('Error refreshing slots:', error);
      }
    }
  };

  const handleCloseSuccess = () => {
    setBookedAppointment(null);
    // In embed mode, just reset the booking state; don't navigate away
    if (!isEmbedMode) {
      navigate('/marketplace');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pure-white"></div>
      </div>
    );
  }

  if (notFound || !doctor) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          {/* 404 Icon */}
          <div className="w-24 h-24 bg-slate-200 dark:bg-silver/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-12 h-12 text-slate-400 dark:text-silver/40" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>

          {/* 404 Title */}
          <h1 className="font-display text-5xl italic text-light-text dark:text-pure-white mb-2" data-testid="not-found-title">404</h1>
          <h2 className="font-display text-2xl italic text-light-text dark:text-pure-white mb-4">Doctor Not Found</h2>

          {/* Description */}
          <p className="text-light-text-secondary dark:text-silver/60 mb-8">
            The doctor profile you're looking for doesn't exist or has been removed.
            This may happen if the doctor has left the platform or their account has been deleted.
          </p>

          {/* Actions */}
          <div className="space-y-3">
            <a
              href="/marketplace"
              className="btn-primary w-full inline-block"
              data-testid="marketplace-link"
            >
              Browse Marketplace
            </a>
            <button
              onClick={() => navigate(-1)}
              className="w-full py-3 px-6 border border-light-border dark:border-border rounded-xl text-light-text dark:text-silver hover:bg-slate-100 dark:hover:bg-silver/5 transition-smooth"
            >
              Go Back
            </button>
          </div>

          {/* Help text */}
          <p className="text-slate-400 dark:text-silver/40 text-sm mt-6">
            Need help? <a
              href="mailto:support@calendiary.com"
              className={`text-light-text dark:text-silver transition-smooth ${
                isEmbedMode && embedPrimaryColor ? '' : 'hover:text-light-text-secondary dark:hover:text-pure-white'
              }`}
              style={isEmbedMode && embedPrimaryColor ? { color: embedPrimaryColor } : {}}
            >Contact Support</a>
          </p>
        </div>
      </div>
    );
  }

  // Get doctor name (handle both API format and mock format)
  const doctorName = doctor.name || `Dr. ${doctor.firstName} ${doctor.lastName}`;

  // Parse specialties - may be a JSON string, array, or null/undefined
  let parsedSpecialties = [];
  if (doctor.specialties) {
    if (Array.isArray(doctor.specialties)) {
      parsedSpecialties = doctor.specialties;
    } else if (typeof doctor.specialties === 'string') {
      try {
        const parsed = JSON.parse(doctor.specialties);
        parsedSpecialties = Array.isArray(parsed) ? parsed : [parsed];
      } catch (e) {
        // If not valid JSON, treat as a single specialty string
        parsedSpecialties = [doctor.specialties];
      }
    }
  }

  const doctorSpecialty = doctor.specialty || (parsedSpecialties.length > 0 ? parsedSpecialties[0] : 'General Practice');
  // Use custom link-in-bio bio if available, otherwise use main bio
  const doctorBio = doctor.linkInBioBio || doctor.bio || '';
  const doctorRating = doctor.rating || doctor.averageRating || 0;
  const doctorReviewCount = doctor.reviewCount || 0;
  const doctorServices = doctor.services || [];

  // Container styles for embed mode with custom colors and font
  const containerStyle = isEmbedMode ? {
    backgroundColor: embedBgColor || 'transparent',
    '--embed-primary-color': embedPrimaryColor || undefined,
    ...(embedFontFamily && { fontFamily: `${embedFontFamily}, sans-serif` }),
  } : {};

  return (
    <div
      className={isEmbedMode ? 'pb-4' : 'min-h-screen bg-light-bg dark:bg-obsidian pb-16'}
      style={containerStyle}
      data-embed-mode={isEmbedMode ? 'true' : undefined}
    >
      {/* Open Graph Meta Tags for Social Media Sharing */}
      <Helmet>
        <title>{doctorName} - {doctorSpecialty} | Calendiary</title>
        <meta name="description" content={doctorBio || `Book an appointment with ${doctorName}, ${doctorSpecialty} specialist.`} />

        {/* Open Graph tags */}
        <meta property="og:title" content={`${doctorName} - ${doctorSpecialty}`} />
        <meta property="og:description" content={doctorBio || `Book an appointment with ${doctorName}, ${doctorSpecialty} specialist.`} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={window.location.href} />
        {doctor.photoUrl && <meta property="og:image" content={doctor.photoUrl} />}

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`${doctorName} - ${doctorSpecialty}`.length <= 70 ? `${doctorName} - ${doctorSpecialty}` : `${doctorName} - ${doctorSpecialty}`.substring(0, 67) + '...'} />
        <meta name="twitter:description" content={(doctorBio || `Book an appointment with ${doctorName}, ${doctorSpecialty} specialist.`).length <= 200 ? (doctorBio || `Book an appointment with ${doctorName}, ${doctorSpecialty} specialist.`) : (doctorBio || `Book an appointment with ${doctorName}, ${doctorSpecialty} specialist.`).substring(0, 197) + '...'} />
        {doctor.photoUrl && <meta name="twitter:image" content={doctor.photoUrl} />}
      </Helmet>

      {/* Success Modal */}
      {bookedAppointment && (
        <SuccessModal
          appointment={bookedAppointment}
          doctorName={doctorName}
          service={selectedService}
          date={selectedDate}
          time={selectedTime}
          onClose={handleCloseSuccess}
        />
      )}

      {/* Doctor Header */}
      <section className={`pb-8 px-6 ${isEmbedMode ? 'pt-4' : 'pt-8'}`}>
        <div className={isEmbedMode ? 'max-w-2xl mx-auto' : 'max-w-2xl mx-auto'}>
          {/* Breadcrumb Navigation - hidden in embed mode */}
          {!isEmbedMode && (
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex items-center gap-2 text-sm font-mono uppercase tracking-wider">
                <li>
                  <Link
                    to="/marketplace"
                    className={`text-light-text-secondary dark:text-silver/60 transition-smooth ${
                      isEmbedMode && embedPrimaryColor ? '' : 'hover:text-light-text dark:hover:text-silver'
                    }`}
                    style={isEmbedMode && embedPrimaryColor ? {} : {}}
                    onMouseEnter={(e) => {
                      if (isEmbedMode && embedPrimaryColor) {
                        e.currentTarget.style.color = embedPrimaryColor;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (isEmbedMode && embedPrimaryColor) {
                        e.currentTarget.style.color = '';
                      }
                    }}
                  >
                    Marketplace
                  </Link>
                </li>
                <li className="text-light-text-secondary dark:text-silver/40" aria-hidden="true">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </li>
                <li>
                  <span className="text-light-text dark:text-silver" aria-current="page">
                    {doctorName}
                  </span>
                </li>
              </ol>
            </nav>
          )}

          <div className="flex flex-col md:flex-row gap-6">
            {/* Doctor Photo */}
            <div className="w-32 h-32 rounded-2xl bg-slate-200 dark:bg-silver/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {doctor.photoUrl ? (
                <img
                  src={doctor.photoUrl}
                  alt={doctorName}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <svg className="w-16 h-16 text-slate-400 dark:text-silver/30" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              )}
            </div>

            {/* Doctor Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <h1
                    className="font-display text-4xl italic text-light-text dark:text-pure-white mb-2 break-words"
                    title={doctorName}
                  >
                    {doctorName}
                  </h1>
                  <p className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
                    {doctorSpecialty}
                  </p>
                  {/* Specialty Tags (if multiple) */}
                  {parsedSpecialties.length > 1 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {parsedSpecialties.map((specialty, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-2 py-1 rounded bg-slate-200 dark:bg-silver/10 text-slate-600 dark:text-silver/70"
                          data-testid={`specialty-tag-${idx}`}
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {/* Favorite Button - Only show for patients */}
                {(!isAuthenticated || user?.role === 'patient') && (
                  <button
                    onClick={handleToggleFavorite}
                    disabled={favoriteLoading}
                    className={`p-3 rounded-xl border transition-smooth flex-shrink-0 ${
                      isFavorited
                        ? (isEmbedMode && embedPrimaryColor ? '' : 'bg-red-500/20 border-red-500/30 text-red-400 hover:bg-red-500/30')
                        : `bg-slate-100 dark:bg-silver/5 border-light-border dark:border-border text-light-text-secondary dark:text-silver/60 ${isEmbedMode && embedPrimaryColor ? '' : 'hover:text-red-400 hover:border-red-400/30'}`
                    }`}
                    style={
                      isFavorited && isEmbedMode && embedPrimaryColor
                        ? {
                            backgroundColor: `${embedPrimaryColor}20`,
                            borderColor: `${embedPrimaryColor}30`,
                            color: embedPrimaryColor,
                          }
                        : !isFavorited && isEmbedMode && embedPrimaryColor
                        ? {
                            '--hover-color': embedPrimaryColor,
                            '--hover-border': `${embedPrimaryColor}30`,
                          }
                        : {}
                    }
                    onMouseEnter={(e) => {
                      if (!isFavorited && isEmbedMode && embedPrimaryColor) {
                        e.currentTarget.style.color = embedPrimaryColor;
                        e.currentTarget.style.borderColor = `${embedPrimaryColor}30`;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isFavorited && isEmbedMode && embedPrimaryColor) {
                        e.currentTarget.style.color = '';
                        e.currentTarget.style.borderColor = '';
                      }
                    }}
                    title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                    data-testid="favorite-button"
                  >
                    {favoriteLoading ? (
                      <svg className="w-6 h-6 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : (
                      <svg className="w-6 h-6" fill={isFavorited ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
              {doctorRating > 0 && (
                <div className="flex items-center gap-1 text-sm text-light-text-secondary dark:text-silver/60 mb-4">
                  <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  {doctorRating.toFixed(1)} ({doctorReviewCount} reviews)
                </div>
              )}
              {doctorBio && doctor.showBio !== false && (
                <p className="text-light-text-secondary dark:text-silver/80">{doctorBio}</p>
              )}

              {/* Book Now Button - Feature #237 */}
              {doctorServices.length > 0 && (
                <button
                  onClick={() => {
                    const servicesSection = document.getElementById('services-section');
                    if (servicesSection) {
                      servicesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className={isEmbedMode && embedPrimaryColor ? "mt-6 px-8 py-3 rounded-xl font-medium transition-smooth shadow-lg hover:shadow-xl" : "mt-6 btn-primary shadow-lg hover:shadow-xl"}
                  style={
                    isEmbedMode && embedPrimaryColor
                      ? primaryButtonStyle
                      : undefined
                  }
                  data-testid="book-now-button"
                >
                  Book Now
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Recently Completed Booking Notification - Back Button Protection */}
      {recentlyCompletedBooking && (
        <section className="px-6 mb-6">
          <div className="max-w-2xl mx-auto">
            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl" data-testid="booking-already-completed">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-green-400 font-medium">Booking Already Completed</p>
                  <p className="text-green-400/80 text-sm mt-1">
                    You've already completed a booking for {recentlyCompletedBooking.service} on {recentlyCompletedBooking.date} at {recentlyCompletedBooking.time}.
                  </p>
                  <p className="text-green-400/60 text-xs mt-2">
                    If you'd like to book another appointment, please select a new service and time below.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setRecentlyCompletedBooking(null);
                    sessionStorage.removeItem(`booking_completed_${id}`);
                  }}
                  className="text-green-400/60 hover:text-green-400 p-1"
                  aria-label="Dismiss"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Booking Section - single column in embed mode for focused flow */}
      <section className={isEmbedMode ? 'px-4' : 'px-6'} id="services-section">
        <div className={isEmbedMode ? 'max-w-2xl mx-auto' : 'max-w-2xl mx-auto'}>
          {!showPayment ? (
            <div className={`grid gap-8 ${isEmbedMode ? 'grid-cols-1' : 'grid-cols-1'}`}>
              {/* Services */}
              <div className={isEmbedMode ? '' : ''}>
                <h2 className="font-display text-2xl italic text-light-text dark:text-pure-white mb-6">Select a Service</h2>

                {doctorServices.length > 0 ? (
                  <div className="space-y-4">
                    {doctorServices.map((service) => (
                      <button
                        key={service.id}
                        onClick={() => {
                          setSelectedService(service);
                          // Feature #94: Date picker defaults to current date
                          // Automatically select today's date when a service is selected
                          if (!selectedDate) {
                            const today = new Date().toISOString().split('T')[0];
                            setSelectedDate(today);
                          }
                        }}
                        className={`w-full p-4 rounded-xl border text-left transition-smooth ${
                          selectedService?.id === service.id
                            ? 'border-light-text dark:border-pure-white bg-slate-100 dark:bg-silver/5'
                            : 'border-light-border dark:border-border hover:border-light-text/30 dark:hover:border-silver/30'
                        }`}
                        style={selectedService?.id === service.id ? primaryBorderStyle : secondaryBorderStyle}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium text-light-text dark:text-pure-white">{service.name}</h3>
                            <p className="text-sm text-light-text-secondary dark:text-silver/60 mt-1">{service.duration || service.durationMinutes} minutes</p>
                          </div>
                          <p className="font-display text-xl italic text-light-text dark:text-pure-white">£{service.price}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-light-text-secondary dark:text-silver/60">No services available for this doctor.</p>
                )}

                {selectedService && (
                  <>
                    {/* Date Selection */}
                    <h3 className="font-display text-xl italic text-light-text dark:text-pure-white mt-8 mb-4">Select a Date</h3>
                    <div className="flex flex-wrap gap-3">
                      {getAvailableDates().map((date) => (
                        <button
                          key={date.value}
                          onClick={() => setSelectedDate(date.value)}
                          className={`px-4 py-2 rounded-lg border transition-smooth ${
                            selectedDate === date.value
                              ? 'border-light-text dark:border-pure-white bg-slate-200 dark:bg-silver/10 text-light-text dark:text-pure-white'
                              : 'border-light-border dark:border-border text-light-text-secondary dark:text-silver/60 hover:border-light-text/30 dark:hover:border-silver/30'
                          }`}
                          style={selectedDate === date.value ? primaryBorderStyle : secondaryBorderStyle}
                        >
                          {date.display}
                        </button>
                      ))}
                    </div>

                    {/* Booking Error Display */}
                    {bookingError && (
                      <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                        <div className="flex items-start gap-3">
                          <svg className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div className="flex-1">
                            <p className="text-red-400 font-medium">Booking Failed</p>
                            <p className="text-red-400/80 text-sm mt-1">{bookingError}</p>
                            <p className="text-red-400/60 text-sm mt-2">Please select a different time slot.</p>
                          </div>
                          <button
                            onClick={() => setBookingError(null)}
                            className="text-red-400/60 hover:text-red-400"
                            aria-label="Dismiss error"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Time Selection */}
                    {selectedDate && (
                      <>
                        <h3 className="font-display text-xl italic text-light-text dark:text-pure-white mt-8 mb-4">Select a Time</h3>
                        {slotsLoading ? (
                          <div className="flex items-center gap-2 text-light-text-secondary dark:text-silver/60">
                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-light-text-secondary dark:border-silver/60"></div>
                            Loading available times...
                          </div>
                        ) : availableSlots.length > 0 ? (
                          <>
                            <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                              {availableSlots.map((slot, index) => {
                                const isAvailable = slot.available !== false;
                                const displayTime = slot.display || slot.time?.split('T')[1]?.substring(0, 5) || slot.time;
                                // Store the full UTC time for booking, use display for comparison
                                const fullTime = slot.time || displayTime;
                                const isSelected = selectedTime === displayTime || selectedTime === fullTime;
                                return (
                                  <button
                                    key={index}
                                    onClick={() => isAvailable && setSelectedTime(fullTime)}
                                    disabled={!isAvailable}
                                    className={`py-2 rounded-lg border transition-smooth text-center ${
                                      isSelected
                                        ? 'border-light-text dark:border-pure-white bg-slate-200 dark:bg-silver/10 text-light-text dark:text-pure-white'
                                        : isAvailable
                                          ? 'border-light-border dark:border-border text-light-text-secondary dark:text-silver/60 hover:border-light-text/30 dark:hover:border-silver/30'
                                          : 'border-red-500/30 bg-red-500/10 text-red-400/60 cursor-not-allowed line-through'
                                    }`}
                                    style={isSelected ? primaryBorderStyle : (isAvailable ? secondaryBorderStyle : {})}
                                    title={isAvailable ? 'Available' : 'Already booked'}
                                  >
                                    {displayTime}
                                  </button>
                                );
                              })}
                            </div>

                            {/* Legend for slots */}
                            {availableSlots.some(s => !s.available) && (
                              <div className="mt-4 flex items-center gap-4 text-xs text-light-text-secondary dark:text-silver/60">
                                <div className="flex items-center gap-2">
                                  <div className="w-4 h-4 rounded border border-light-border dark:border-border"></div>
                                  <span>Available</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="w-4 h-4 rounded border border-red-500/30 bg-red-500/10"></div>
                                  <span>Already booked</span>
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="space-y-3">
                            <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                              <svg className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div>
                                <p className="font-body text-sm text-yellow-200 font-medium mb-1">
                                  No Availability
                                </p>
                                <p className="font-body text-xs text-yellow-200/80">
                                  This doctor currently has no available appointment slots for this date. Please try selecting another date or check back later.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Booking Summary - inline in embed mode, sticky sidebar otherwise */}
              <div className={isEmbedMode ? '' : 'lg:col-span-1'}>
                <div className={`card ${isEmbedMode ? '' : 'sticky top-24'}`}>
                  <h3 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">Your Booking</h3>

                  {selectedService ? (
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-light-text-secondary dark:text-silver/60">Service</span>
                        <span className="text-light-text dark:text-silver">{selectedService.name}</span>
                      </div>
                      {selectedDate && (
                        <div className="flex justify-between">
                          <span className="text-light-text-secondary dark:text-silver/60">Date</span>
                          <span className="text-light-text dark:text-silver">{formatShortDate(selectedDate)}</span>
                        </div>
                      )}
                      {selectedTime && (
                        <div className="flex justify-between">
                          <span className="text-light-text-secondary dark:text-silver/60">Time</span>
                          <span className="text-light-text dark:text-silver">{selectedTime.includes('T') ? formatTime(selectedTime) : selectedTime}</span>
                        </div>
                      )}
                      <div className="border-t border-light-border dark:border-border pt-3 mt-3">
                        <div className="flex justify-between font-medium">
                          <span className="text-light-text dark:text-silver">Total</span>
                          <span className="text-light-text dark:text-pure-white text-lg">£{selectedService.price}</span>
                        </div>
                      </div>
                      <button
                        onClick={handleBookNow}
                        disabled={!selectedDate || !selectedTime}
                        className="btn-primary w-full mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={primaryButtonStyle}
                      >
                        Confirm Booking
                      </button>

                      {/* Cancellation Policy */}
                      {doctor?.cancellationPolicy && (
                        <div className="mt-4 pt-4 border-t border-light-border dark:border-border" data-testid="cancellation-policy">
                          <h4 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">Cancellation Policy</h4>
                          <ul className="space-y-1 text-xs text-light-text-secondary dark:text-silver/60">
                            <li className="flex items-start gap-2">
                              <span className="text-green-400">&#x2713;</span>
                              <span>Free cancellation {doctor.cancellationPolicy.freeCancellationHours}+ hours before</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-yellow-400">&#x2713;</span>
                              <span>{doctor.cancellationPolicy.partialRefundPercentage}% refund {doctor.cancellationPolicy.partialRefundHours}-{doctor.cancellationPolicy.freeCancellationHours} hours before</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-red-400">&#x2717;</span>
                              <span>No refund under {doctor.cancellationPolicy.noRefundHours} hours</span>
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-slate-400 dark:text-silver/40 text-sm">Select a service to continue</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* Payment Form */
            <div className="max-w-xl mx-auto">
              <h2 className="font-display text-2xl italic text-light-text dark:text-pure-white mb-6">Complete Your Booking</h2>
              <div className="card">
                <PaymentForm
                  doctor={doctor}
                  service={selectedService}
                  selectedDate={selectedDate}
                  selectedTime={selectedTime}
                  onSuccess={handlePaymentSuccess}
                  onCancel={() => setShowPayment(false)}
                  primaryButtonStyle={primaryButtonStyle}
                  embedPrimaryColor={embedPrimaryColor}
                  isEmbedMode={isEmbedMode}
                  bookingSource={bookingSource}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Reviews Section - hidden in embed mode, or if doctor has disabled it */}
      {!showPayment && !isEmbedMode && doctor?.showReviews !== false && (
        <section className="px-6 mt-12">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-display text-2xl italic text-light-text dark:text-pure-white mb-6">
              Patient Reviews
              {doctorReviewCount > 0 && (
                <span className="text-light-text-secondary dark:text-silver/60 text-lg ml-2">({doctorReviewCount})</span>
              )}
            </h2>

            {reviewsLoading ? (
              <div className="flex items-center gap-2 text-light-text-secondary dark:text-silver/60">
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-light-text-secondary dark:border-silver/60"></div>
                Loading reviews...
              </div>
            ) : reviews.length > 0 ? (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div key={review.id} className="card" data-testid="review-card" data-rating={review.rating}>
                    <div className="flex items-start gap-4">
                      {/* Reviewer Avatar */}
                      <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-silver/10 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-slate-400 dark:text-silver/30" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                        </svg>
                      </div>

                      {/* Review Content */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-medium text-light-text dark:text-pure-white">{review.patientName}</span>
                          <StarRating rating={review.rating} />
                          <span className="text-slate-400 dark:text-silver/40 text-sm" data-testid="review-rating-value">
                            {review.rating} stars
                          </span>
                        </div>

                        {review.comment && (
                          <p className="text-light-text-secondary dark:text-silver/80 mb-2">{review.comment}</p>
                        )}

                        <p className="text-slate-400 dark:text-silver/40 text-xs">
                          {formatDate(review.createdAt)}
                        </p>

                        {/* Doctor Response */}
                        {review.doctorResponse && (
                          <div className="mt-4 pl-4 border-l-2 border-slate-300 dark:border-silver/20">
                            <p className="text-sm text-light-text-secondary dark:text-silver/60 mb-1">Response from the doctor:</p>
                            <p className="text-light-text-secondary dark:text-silver/80">{review.doctorResponse}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="card text-center py-8">
                <svg className="w-12 h-12 text-slate-400 dark:text-silver/30 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <p className="text-light-text-secondary dark:text-silver/60">No reviews yet</p>
                <p className="text-slate-400 dark:text-silver/40 text-sm mt-1">Be the first to review this doctor after your appointment!</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Powered by Calendiary Badge - shown in embed mode for Free tier */}
      {showPoweredByBadge && (
        <div className="fixed bottom-4 right-4 z-50" data-testid="powered-by-badge">
          <a
            href="https://calendiary.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-charcoal border border-slate-200 dark:border-border rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 text-sm"
          >
            <svg className="w-4 h-4 text-slate-600 dark:text-silver" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="font-mono text-xs uppercase tracking-wider text-slate-500 dark:text-silver/60">
              Powered by
            </span>
            <span className="font-display text-sm italic text-slate-700 dark:text-pure-white">
              Calendiary
            </span>
          </a>
        </div>
      )}
    </div>
  );
};

export default DoctorProfile;
